
import { Component, Watch } from "vue-property-decorator";
import { PercentIndicator } from "@/mixins/percentIndicatorMixin";
import DateFormatter from "@/mixins/dateFormatterMixin";
import BotTypeStats from "@/models/public-bot-stats/BotTypeStats";
import {
  TimePeriod,
  toTimePeriodString,
  toTimePeriod,
} from "@/models/public-bot-stats/TimePeriod";
import {
  BotDuration,
  toBotDurationString,
  toBotDuration,
} from "@/models/public-bot-stats/BotDuration";
import AppNavBar from "@/components/app/AppNavBar.vue";
import BaseView from "@/views/BaseView.vue";
import TooltipIcon from "@/components/general/TooltipIcon.vue";
import FunctionalityLimitation from "@/mixins/functionalityLimitationMixin";
import MarketSlot from "@/components/general/MarketSlot.vue";
import OBvsTopMarketsChart from "./components/OBvsTopMarketsChart.vue";
import TradesMadeChart from "./components/TradesMadeChart.vue";
import DataTableWithDraggableColumnsDirective from "@/directives/dataTableWithDraggableColumns";
import * as DataTableUtils from "@/utils/dataTableUtils";
import FunctionalityLimitationMixin from "@/mixins/functionalityLimitationMixin";

@Component({
  components: {
    AppNavBar,
    TooltipIcon,
    MarketSlot,
    OBvsTopMarketsChart,
    TradesMadeChart,
  },
  mixins: [PercentIndicator, DateFormatter, FunctionalityLimitation],
  directives: {
    "sortable-table": DataTableWithDraggableColumnsDirective(),
  },
})
export default class BotStatsOverview extends BaseView {
  // 📦 Headers displayed for the bots stats table
  private botStatsHeaders: any[] = [
    // { text: "", sortable: false, value: "image" },
    { text: "Bot Type", value: "name" },
    { text: "ROI 30d", value: "monthRoi" },
    { text: "ROI 7d", value: "weekRoi" },
    { text: "ROI 24h", value: "dayRoi" },
    { text: "ROI / Trade", value: "averageRoiPerTrade" },
    { text: "AVG Sharpe", value: "averageSharpe" },
    { text: "AVG Sortino", value: "averageSortino" },
    { text: "AVG MDD", value: "averageMaximumDrawdown" },
    { text: "", sortable: false, value: "action" },
  ];

  // 📦 Available time periods by which the results are filtered
  private timePeriods = [
    { text: "Last 24 Hours", value: TimePeriod.Day },
    { text: "Last 7 Days", value: TimePeriod.Week },
    { text: "Last 30 Days", value: TimePeriod.Month },
    { text: "Last 90 Days", value: TimePeriod.ThreeMonths },
    { text: "Last 180 Days", value: TimePeriod.SixMonths },
    { text: "Since Start", value: TimePeriod.FromStart },
  ];

  // 📦 Available duration periods by which the bot stats should be filtered
  private durations = [
    { text: "All", value: BotDuration.All },
    { text: "1 Week", value: BotDuration.Week },
    { text: "2 Weeks", value: BotDuration.TwoWeeks },
    { text: "1 Month", value: BotDuration.Month },
    { text: "3 Months", value: BotDuration.ThreeMonths },
  ];

  // 📃 Possible time filters for the chart
  private chartFilters: any = {
    "24H": TimePeriod.Day,
    "7D": TimePeriod.Week,
    "30D": TimePeriod.Month,
    "90D": TimePeriod.ThreeMonths,
  };

  // 📦 Selected timeframe in seconds for the trades made chart
  private selectedTradesMadeFilterInDays: number = TimePeriod.Week;

  // 📦 Selected timeframe in seconds for the OCC vs markets chart
  private obVsMarketFilterInDays: number = TimePeriod.FromStart;

  // 📦 The stats for all available bots
  private get botsStats(): BotTypeStats[] {
    return this.$store.state.publicBotStats.botTypesStats;
  }

  // 📦 Selected time period from the selector
  private selectedTimePeriod: TimePeriod = TimePeriod.Month;

  // 📦 Mode which represents whether the results should be only active bots or all
  private selectedMode: number = 0;

  // 📦 The selected market the user wants to filter the stats by
  private selectedMarketToFilterBy: string = "All";

  // 📦 The selected bot life duration the user wants to filter the stats by
  private selectedDurationToFilterBy: BotDuration = BotDuration.All;

  // 📦 The last time the general statistics were last updated
  private generalStatisticsLastUpdateTime: number | null = null;

  // 🚩 Flag for whether the bot stats are loading
  private get isLoading(): boolean {
    return this.$store.state.publicBotStats.generalBotsStatsAreLoading;
  }

  // 📦 The available markets the user could filter the stats by
  private get availableMarkets(): string[] {
    let allMarkets: string[] = Array.from(
      this.$store.getters.allAvailableMarkets
    );
    allMarkets = allMarkets.sort();
    allMarkets.unshift("All");
    return allMarkets;
  }

  /**
   * 👂 Listener for changes in the selected trades made time filter
   */
  @Watch("selectedTradesMadeFilterInDays")
  private onSelectedTradesMadeFilterChanged(val: number, oldVal: number) {
    this.obVsMarketFilterInDays = new FunctionalityLimitationMixin().isAdminUser
      ? val
      : TimePeriod.FromStart;
  }

  /**
   * 👂 Listener for changes in the selected mode
   */
  @Watch("selectedMode")
  private onSelectedModeChanged(val: number, oldVal: number) {
    this.modifyRoute();
  }

  /**
   * 👂 Listener for changes in the selected time period
   */
  @Watch("selectedTimePeriod")
  private onSelectedTimePeriodChanged(val: TimePeriod, oldVal: TimePeriod) {
    this.modifyRoute();
  }

  /**
   * 👂 Listener for changes in the selected market
   */
  @Watch("selectedMarketToFilterBy")
  private onSelectedMarketChange(val: string, oldVal: string) {
    this.modifyRoute();
  }

  /**
   * 👂 Listener for changes in the selected duration
   */
  @Watch("selectedDurationToFilterBy")
  private onSelectedDurationCHange(val: BotDuration, oldVal: BotDuration) {
    this.modifyRoute();
  }

  /**
   * 🔨 Changes the browser URL
   */
  private modifyRoute() {
    this.fetchBotStats(
      this.selectedTimePeriod,
      Boolean(this.selectedMode),
      this.selectedDurationToFilterBy,
      this.selectedMarketToFilterBy
    );
    this.$router.replace({
      query: {
        timePeriod: toTimePeriodString(this.selectedTimePeriod),
        enabledOnly: String(Boolean(this.selectedMode)),
        minimumDuration: toBotDurationString(this.selectedDurationToFilterBy),
        market: this.selectedMarketToFilterBy,
      },
    });
  }

  /**
   * 🚀 Fetched the public bot statistics for each strategy
   */
  private fetchBotStats(
    timePeriod: TimePeriod,
    enabledOnly: boolean,
    minimumDuration: BotDuration,
    market: string
  ) {
    this.$store.dispatch("fetchGeneralPublicBotStats", {
      timePeriod: timePeriod == -1 ? null : timePeriod,
      enabledOnly: enabledOnly,
      minimumDuration: minimumDuration == -1 ? null : minimumDuration,
      market: market === "All" ? null : market,
    });
  }

  protected async mounted() {
    await super.mounted();

    // Add Bot vs market for admin users
    if (new FunctionalityLimitation().isPremiumAccessUser) {
      this.botStatsHeaders.splice(1, 0, {
        text: "New Bots",
        value: "numberOfBots",
      });

      this.botStatsHeaders.splice(2, 0, {
        text: "Trades Made",
        value: "totalTrades",
      });

      this.botStatsHeaders.splice(3, 0, {
        text: "ROI / Month",
        value: "averageRoiPerMonth",
      });

      this.botStatsHeaders.splice(this.botStatsHeaders.length - 1, 0, {
        text: "AI Outperforming The Market",
        value: "botsOutperformingTheMarket",
      });

      this.botStatsHeaders.splice(this.botStatsHeaders.length - 1, 0, {
        text: "Best Performing Pairs",
        value: "bestPerformingMarkets",
      });
    }

    if (new FunctionalityLimitation().isAdminUser) {
      this.chartFilters["180D"] = TimePeriod.SixMonths;
      this.chartFilters["Since Start"] = TimePeriod.FromStart;

      this.botStatsHeaders.splice(this.botStatsHeaders.length - 1, 0, {
        text: "AVG Bot vs Market ROI Per Month",
        value: "averageMonthlyOutperformance",
      });
    }

    // Get query parameters
    this.selectedTimePeriod = toTimePeriod(this.$route.query.timePeriod);
    this.selectedMode = Number(this.$route.query.enabledOnly === "true");
    this.selectedMarketToFilterBy = this.$route.query.market
      ? (this.$route.query.market as string)
      : "All";
    this.selectedDurationToFilterBy = toBotDuration(
      this.$route.query.minimumDuration
    );
    this.modifyRoute();

    this.$store.dispatch("fetchAllAvailableExchanges");
    this.fetchBotStats(
      this.selectedTimePeriod,
      Boolean(this.selectedMode),
      this.selectedDurationToFilterBy,
      this.selectedMarketToFilterBy
    );
  }

  /**
   * 👂 Opens specific bot stats when a row of the data table is clicked
   */
  private onRowClicked(botTypeStats: BotTypeStats) {
    this.$router.push({
      name: "AI Performance",
      params: { id: botTypeStats.botStrategy },
      query: {
        timePeriod: toTimePeriodString(this.selectedTimePeriod),
        minimumDuration: toBotDurationString(this.selectedDurationToFilterBy),
        market: this.selectedMarketToFilterBy,
      },
    });
  }

  // 📦 The column index of the dragged columns
  private dataTableColumnIndex: number = 1;

  /**
   * 🏗 Sorts the headers of the data table based on the draggable event
   */
  private sortTheHeadersAndUpdateTheKey(evt: any) {
    DataTableUtils.sortTheHeadersAndUpdateTheKey(evt, this.botStatsHeaders);
    this.dataTableColumnIndex += 1;
  }
}
